const removeByKeys = function(handlers, keys)
{
    let i = keys.length;
    while (i--)
    {
        handlers.splice(keys[i], 1);
    }
};

const removeByChannel = function(handlers, channel)
{
    if (!Array.isArray(handlers)) { return; }
    let i = handlers.length;
    while (i--)
    {
        if (handlers[i].channel === channel)
        {
            handlers.splice(i, 1);
        }
    }
};

/** Optionen für einen Event-Handler
 * @typedef {Object} Eventable~HandlerOptions
 * @property {string=} channel - Verbinde diesen Handler mit einem Kanal, den man beim Event-Feuern ansprechen kann oder
 * der einem beim Abhängen von Events hilft.
 * @property {Object=} scope - Bestimme ``this`` während des Handler-Aufrufs. Wenn nicht definiert, ist ``this`` das Objekt,
 * an dem der Handler hängt.
 * @property {number=} count - Anzahl der Aufrufe, bevor Handler abgehangen wird. Wird bei once-Methode ignoriert.
 * @property {Object=} event - Ein Template für das Event-Objekt für diesen Handler.
 */

/** Dieses Mixin erweitert Objekte oder Klassen um ein Eventsystem. Es ist dann möglich, per on- und off-Methode Eventlistener
 * an- und abzuhängen und mit der fire-Methode die Handler auszulösen.
 * @mixin Eventable
 */
const Eventable = superClass => class extends superClass
{
    #events = {};

    get _events()
    {
        console.warn('_events will be private.');
        return this.#events;
    }

    /** Registriert eine Handler-Funktion, die immer dann aufgerufen wird, wenn das Event gefeuert wird.
     * @memberof Eventable#
     * @param {string} types - Name des Events
     * @param {Function} handler - Der Event-Handler
     * @param {Eventable~HandlerOptions=} options
     */
    on(types, handler, options = {})
    {
        types = types.split(' ');

        options.event = options.event || {};
        if (options.channel)
        {
            options.event.channel = options.channel;
        }

        for (const name of types)
        {
            const handlers = this.#events[name] || [];
            handlers.push({
                handler,
                scope: options.scope,
                count: options.count || Infinity,
                event: options.event,
                channel: options.channel,
                fired: 0
            });
            this.#events[name] = handlers;
        }
        return this;
    }

    /** Registriert eine Handler-Funktion, die nur einmal aufgerufen wird
     * @memberof Eventable#
     * @param {string} types - Name des Events
     * @param {Function} handler - Der Event-Handler
     * @param {Eventable~HandlerOptions=} options
     */
    once(types, handler, options = {})
    {
        options.count = 1;
        return this.on(types, handler, options);
    }

    /** Hängt die übergebene Handler-Funktion vom Event ab oder hängt alle Handler zu einem bestimmten Event ab,
     * falls nur der Event-Name übergeben wird.
     * @memberof Eventable#
     * @param {string} type - Name des Events
     * @param {Function=} handler - Der Event-Handler
     */
    off(type, handler)
    {
        if (this.#isTypeRegistered(type))
        {
            if (handler === undefined)
            {
                delete this.#events[type];
            }
            else
            {
                const events = this.#events[type];
                let i = events.length;

                while (i--)
                {
                    if (events[i].handler === handler)
                    {
                        events.splice(i, 1);
                    }
                }
            }
        }

        return this;
    }

    removeAllEventListeners()
    {
        console.warn('Please use "removeAllListeners" instead of "removeAllEventListeners".');
        return this.removeAllListeners();
    }

    /** Hängt alle Handler zu allen Events von diesen Objekt ab.
     * @memberof Eventable#
     */
    removeAllListeners()
    {
        Object.values(this.#events).forEach(type => this.off(type));
        return this;
    }

    /** Entfernt nur die Handler in einem bestimmten Channel.
     * @memberof Eventable#
     * @param {string} channel - Name des Channels
     * @param {string=} type - Falls nur bei einem bestimmten Event-Typ die Handler abgehangen werden sollen.
     */
    removeListenersByChannel(channel, type)
    {
        const events = this.#events;
        const types = type ? [type] : Object.keys(events);
        types.forEach(t => removeByChannel(events[t], channel));
        return this;
    }

    /** Feuert ein Event und löst alle registrierten Handler aus.
     * @memberof Eventable#
     * @param {string} type - Name des Events
     * @param {Object=} event - Das Event-Objekt, das der Handler-Funktion übergeben werden soll. Type wird
     * automatisch gesetzt. Hat das Event einen Channel, werden nur die Handler in diesem Channel aufgerufen.
     */
    fire(type, event = {})
    {
        if (!this.#isTypeRegistered(type))
        {
            return this;
        }

        event.type = type;
        const handlers = this.#events[type];
        const {length} = handlers;
        const {channel} = event;
        const handlersToCall = [];
        const indicesToRemove = [];

        for (let i = 0; i < length; i++)
        {
            const handler = handlers[i];
            if (channel && channel !== handler.channel) { continue; }

            handlersToCall.push(handler);
            handler.fired++;
            if (handler.fired === handler.count)
            {
                indicesToRemove.push(i);
            }
        }

        removeByKeys(handlers, indicesToRemove);

        handlersToCall.forEach(
            handler => handler.handler.call(handler.scope || this, {...handler.event, ...event})
        );

        return this;
    }

    /** Überprüft, ob Listeners angehangen sind, optional für einen übergebenen Typ
     * @memberof Eventable#
     * @param {string=} type
     */
    hasListeners(type)
    {
        const events = this.#events;
        if (type)
        {
            if (!this.#isTypeRegistered(type))
            {
                return false;
            }
            return !!events[type].length;
        }
        return Object.values(events).some(event => !!event.length);
    }

    #isTypeRegistered(type)
    {
        return this.#events.hasOwnProperty(type);
    }
};

Eventable.sorting = 3;

export default Eventable;
